import { useMutation, useQueryClient } from '@tanstack/react-query'

import { addSpaceMembership } from '~/server/event/event-space-lists.service'
import { fireCustomerIOEvent } from '~/client/common/customerio'
import { CustomerIOEventName } from '~/shared/customerio/customerio.types'

/** Discovery surface the space came from — feeds the `source` Customer.io property. */
export type AddSpaceSource = 'browse' | 'recs' | 'collection' | 'ghost' | 'manual'

type AddSpaceToEventVariables = {
  eventId: number
  spaceId?: number
  userSpaceId?: number
  source?: AddSpaceSource
}

export const useAddSpaceToEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['addSpaceToEvent'],
    mutationFn: async ({ eventId, spaceId, userSpaceId, source }: AddSpaceToEventVariables) => {
      if (!eventId) throw new Error('eventId is required')
      if (!spaceId && !userSpaceId) throw new Error('spaceId or userSpaceId is required')
      const result = await addSpaceMembership({
        event_space_list_id: eventId,
        space_id: typeof spaceId === 'number' ? spaceId : null,
        user_space_id: typeof userSpaceId === 'number' ? userSpaceId : null,
      })
      if (!result.alreadyExists) {
        void fireCustomerIOEvent(CustomerIOEventName.SpaceAddedToEvent, {
          event_id: eventId,
          space_id: spaceId ?? null,
          user_space_id: userSpaceId ?? null,
          source: source ?? 'manual',
        })
      }
      return result
    },
    onSuccess: (_data, variables) => {
      // Refresh event lists and all event detail queries
      // We invalidate all ['event'] queries because the event page can be accessed
      // via numeric ID (/events/123) or slug (/events/my-slug), and we need to
      // ensure both query key variants are invalidated
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['event'] })
    },
  })
}
